// src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { NavigationProvider, useNavigation } from './context/NavigationContext';
import ProtectedRoute from './redux/ProtectedRoute';
import { PermissionsProvider } from './redux/PermissionsContext';
import { ChatCountsProvider } from './context/ChatCountsContext';
import PrivateRoute from './pages/auth/PrivateRoute';
import Dashboard from './pages/Dashboard';
import ProductList from './pages/Products/ProductList';
import ProductEdit from './pages/Products/ProductEdit';
import ProductAdd from './pages/Products/ProductAdd';
import QuestionAdd from './pages/Questions/QuestionAdd';
import QuestionList from './pages/Questions/QuestionList';
import QuestionEdit from './pages/Questions/QuestionEdit';
import AssignQuestions from './pages/Products/AssignQuestions';
import TemplateList from './pages/AssessmentTemplates/TemplateList';
import TemplateEditor from './pages/AssessmentTemplates/TemplateEditor';
import ProductRules from './pages/AssessmentTemplates/ProductRules';
import OrderList from './pages/Orders/OrderList';
import OrderDetail from './pages/Orders/OrderDetail';
import OrderEdit from './pages/Orders/OrderEdit';
import PharmaOrders from './pages/Orders/PharmaOrders';
import SignPostOrders from './pages/Orders/SignPost/SignPostOrders';
import Questionnaire from './pages/assessments/Questionnaire';
import VariationList from './pages/Variations/VariationList';
import OrderAssessment from './pages/Orders/OrderAssessment';
import Login from './pages/auth/Login';
import MFA from './pages/auth/MFA';
import ChangePassword from './pages/auth/ChangePassword';
import Profile from './pages/Profile';
import Notes from './pages/notes/Notes';
import DemoAssessment from './pages/assessments/DemoAssessment';
import Orders from './pages/Orders/AssessmentOrder/Orders';
import DemoChat from './pages/Orders/DemoChat';
import Assessment from './pages/Orders/Assessment/Assessment';
import EbayQuestionDemo from './pages/assessments/EbayQuestionDemo';
import Add from './pages/externalLogs/nearMissLogs/Add';
import List from './pages/externalLogs/nearMissLogs/List';
import Edit from './pages/externalLogs/nearMissLogs/Edit';
import FileList from './pages/externalLogs/fileManager/FileList';
// Users + role/permission management migrated to inventory admin UI.
// Page components retained on disk (`pages/users/*`, `pages/RolePermission/*`);
// imports + routes disabled so users + permissions are managed solely via central /access-control.
// To revert: restore these imports and the Route blocks below.
// import UserList from './pages/users/UserList';
// import UserAdd from './pages/users/UserAdd';
// import UserEdit from './pages/users/UserEdit';
import MeetingAdd from './pages/meetings/meetingAdd';
import MeetingList from './pages/meetings/meetingList';
import StaffMeeting from './pages/meetings/StaffMeeting';
import MeetingEdit from './pages/meetings/MeetingEdit';
import MeetingDetail from './pages/meetings/MeetingDetail';
import SiteSetting from './pages/settings/SiteSetting';
import AssessmentTracker from './pages/Orders/Assessment/AssessmentTracker';
import Shifts from './pages/shift/Shifts';
import Event from './pages/shift/Event';
// import Roles from './pages/RolePermission/Roles';
// import Permission from './pages/RolePermission/Permission';
import Unauthorized from './pages/notfound/Unauthorized';
import ChatPage from './pages/chat';
import ChatReport from './pages/chat/ChatReport';
import Inquiries from './pages/inquiries/Inquiries';
import InquiriesDetail from './pages/inquiries/InquiryDetail';
import EscalatedOrders from './pages/Orders/Escalations/EscalatedOrders';
import ScheduledDispatchOrders from './pages/Orders/ScheduledDispatch/ScheduledDispatchOrders';

import IncidentLogAdd from './pages/externalLogs/incidentLogs/Add';
import IncidentLogEdit from './pages/externalLogs/incidentLogs/Edit';
import IncidentLogList from './pages/externalLogs/incidentLogs/List';
import MissingValue from './pages/MissingValue';
import PostageList from './pages/postage/PostageList';
import CourierJobs from './pages/Orders/CourierJobs';
import CourierJobsStudio from './pages/Orders/CourierJobs/preview/CourierJobsStudio';
import StockDashboard from './pages/StockDashboard';
import TrackingPage from './pages/Tracking/TrackingPage';
import TrackingFailureLogs from './pages/Tracking/TrackingFailureLogs';
import ReturnsList from './pages/Returns/ReturnsList';
import CatdReplyReport from './pages/Orders/CatdReplies/CatdReplyReport';

import ComplaintsList from './pages/complaints/ComplaintsList';
import ComplaintDetail from './pages/complaints/ComplaintDetail';

const App = () => {

    return (
        <PermissionsProvider>
            <ChatCountsProvider>
                <Router>
                    <Routes>

                        <Route path='/stock-summary' element={<ProtectedRoute component={StockDashboard} requiredPermissions={['general.inventory']} />} />
                        <Route path='/login' element={<Login />} />
                        <Route path='/mfa' element={<MFA />} />
                        <Route path='/change-password' element={<PrivateRoute><ChangePassword /></PrivateRoute>} />
                        <Route path='/unauthorized' element={<Unauthorized />} />
                        <Route path='/questionnaire/:id' element={<Questionnaire />} />
                        <Route path='/tracking-failures/:logId' element={<TrackingFailureLogs />} />
                        <Route path='/' element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                        <Route path='/missing-values' element={<PrivateRoute><MissingValue /></PrivateRoute>} />

                        {/* Product routes  */}
                        <Route path='/products' element={<ProtectedRoute component={ProductList} requiredPermissions={['product.view']} />} />
                        <Route path='/products/add' element={<ProtectedRoute component={ProductAdd} requiredPermissions={['product.edit']} />} />
                        <Route path='/products/edit/:id' element={<ProtectedRoute component={ProductEdit} requiredPermissions={['product.edit']} />} />
                        <Route path='/products/assign-question/:id' element={<ProtectedRoute component={AssignQuestions} requiredPermissions={['product.assign_question']} />} />
                        <Route path='/products/:id/assessment-rules' element={<ProtectedRoute component={ProductRules} requiredPermissions={['product.assign_templates']} />} />
                        <Route path='/variations' element={<ProtectedRoute component={VariationList} requiredPermissions={['product.variations']} />} />

                        {/* Question Routes  */}
                        <Route path='/questions/add' element={<ProtectedRoute component={QuestionAdd} requiredPermissions={['question.create']} />} />
                        <Route path='/questions' element={<ProtectedRoute component={QuestionList} requiredPermissions={['question.view']} />} />
                        <Route path='/questions/edit/:id' element={<ProtectedRoute component={QuestionEdit} requiredPermissions={['question.edit']} />} />

                        <Route path='/assessment-templates' element={<ProtectedRoute component={TemplateList} requiredPermissions={['question.view_templates']} />} />
                        <Route path='/assessment-templates/new' element={<ProtectedRoute component={TemplateEditor} requiredPermissions={['question.add_template']} />} />
                        <Route path='/assessment-templates/:id' element={<ProtectedRoute component={TemplateEditor} requiredPermissions={['question.edit_template']} />} />

                        {/* Order routes  */}
                        <Route path='/orders' element={<ProtectedRoute component={OrderList} requiredPermissions={['order.all_orders']} />} />
                        <Route path='/orders/report' element={<ProtectedRoute component={CatdReplyReport} requiredPermissions={['order.pgd_catd_report']} />} />
                        <Route path='/orders/edit/:id' element={<ProtectedRoute component={OrderEdit} requiredPermissions={['order.edit']} />} />
                        <Route path='/orders/pharma-medicine' element={<ProtectedRoute component={PharmaOrders} requiredPermissions={['order.pending_p_approval']} />} />
                        <Route path='/orders/:id' element={<ProtectedRoute component={Assessment} requiredPermissions={['order.detail']} />} />
                        <Route path='/assessment/tracker/:id' element={<ProtectedRoute component={AssessmentTracker} requiredPermissions={['order.tracker']} />} />
                        <Route path='/orders/assessment-required' element={<ProtectedRoute component={Orders} requiredPermissions={['order.assessment_incomplete']} />} />
                        <Route path='/orders/repeat-offenders' element={<ProtectedRoute component={SignPostOrders} requiredPermissions={['order.all_orders']} />} />
                        <Route path='/orders/courier-jobs' element={<ProtectedRoute component={CourierJobs} requiredPermissions={['order.courier_jobs']} />} />
                        <Route path='/orders/courier-jobs/new' element={<ProtectedRoute component={CourierJobsStudio} requiredPermissions={['order.courier_jobs_new']} />} />


                        <Route path='/assessment/demo/:id' element={<PrivateRoute><DemoAssessment /></PrivateRoute>} />
                        <Route path='/chat/demo' element={<PrivateRoute><DemoChat /></PrivateRoute>} />
                        <Route path='/assessment/demo' element={<PrivateRoute><Assessment /></PrivateRoute>} />
                        <Route path='/ebay/question/demo/:id' element={<PrivateRoute><EbayQuestionDemo /></PrivateRoute>} />

                        {/* Logs routes  */}
                        <Route path='/near-miss-logs/add' element={<ProtectedRoute component={Add} requiredPermissions={['external_log.near_miss_view']} />} />
                        <Route path='/near-miss-logs' element={<ProtectedRoute component={List} requiredPermissions={['external_log.near_miss_create']} />} />
                        <Route path='/near-miss-logs/edit/:id' element={<ProtectedRoute component={Edit} requiredPermissions={['external_log.near_miss_edit']} />} />
                        <Route path='/file-manager' element={<ProtectedRoute component={FileList} requiredPermissions={['external_log.file_manager']} />} />

                        <Route path='/incident-logs/add' element={<ProtectedRoute component={IncidentLogAdd} requiredPermissions={['external_log.near_miss_view']} />} />
                        <Route path='/incident-logs' element={<ProtectedRoute component={IncidentLogList} requiredPermissions={['external_log.near_miss_create']} />} />
                        <Route path='/incident-logs/edit/:id' element={<ProtectedRoute component={IncidentLogEdit} requiredPermissions={['external_log.near_miss_edit']} />} />

                        {/* /users + /roles routes removed: managed via inventory admin UI now. */}

                        {/* Meeting routes  */}
                        <Route path='/meetings/add' element={<ProtectedRoute component={MeetingAdd} requiredPermissions={['meeting.create']} />} />
                        <Route path='/meetings' element={<ProtectedRoute component={MeetingList} requiredPermissions={['meeting.management_meeting']} />} />
                        <Route path='/meetings/staff' element={<ProtectedRoute component={StaffMeeting} requiredPermissions={['meeting.staff_meeting']} />} />
                        <Route path='/meetings/edit/:id' element={<ProtectedRoute component={MeetingEdit} requiredPermissions={['meeting.edit']} />} />
                        <Route path='/meetings/detail/:id' element={<ProtectedRoute component={MeetingDetail} requiredPermissions={['meeting.view']} />} />
                        <Route path='/notes' element={<ProtectedRoute component={Notes} requiredPermissions={['general.notes']} />} />



                        {/* setting routes  */}
                        <Route path='/site-setting' element={<ProtectedRoute component={SiteSetting} requiredPermissions={['setting.site_setting']} />} />
                        <Route path='/events' element={<ProtectedRoute component={Event} requiredPermissions={['setting.events']} />} />
                        <Route path='/profile' element={<ProtectedRoute component={Profile} requiredPermissions={['setting.profile_setting']} />} />


                        {/* Role routes removed: managed via inventory admin UI now. */}

                        {/* chat routes  */}
                        <Route path='/chat' element={<ProtectedRoute component={ChatPage} requiredPermissions={['order.view_message']} />} />
                        <Route path='/chat/report' element={<ProtectedRoute component={ChatReport} requiredPermissions={['order.chat_report']} />} />

                        {/* Inquiries routes  */}
                        <Route path='/cases' element={<ProtectedRoute component={Inquiries} requiredPermissions={['general.case_view']} />} />
                        <Route path='/cases/:id' element={<ProtectedRoute component={InquiriesDetail} requiredPermissions={['general.case_view']} />} />
                        <Route path='/escalations' element={<ProtectedRoute component={EscalatedOrders} requiredPermissions={['order.escalation_view']} />} />
                        <Route path='/pharmacy-escalations' element={<ProtectedRoute key='pharmacy-escalations' component={EscalatedOrders} type='PHARMACY' requiredPermissions={['order.pharmacy_escalation_view']} />} />
                        <Route path='/scheduled-dispatch' element={<ProtectedRoute component={ScheduledDispatchOrders} requiredPermissions={['order.edit']} />} />
                        <Route path='/postages' element={<ProtectedRoute component={PostageList} requiredPermissions={['product.variations']} />} />
                        <Route path='/tracking' element={<ProtectedRoute component={TrackingPage} requiredPermissions={['order.tracker']} />} />
                        <Route path='/returns' element={<ProtectedRoute component={ReturnsList} requiredPermissions={['order.return_create']} />} />

                        {/* Complaints routes */}
                        <Route path='/complaints' element={<ProtectedRoute component={ComplaintsList} requiredPermissions={['general.complaints_view']} />} />
                        <Route path='/complaints/:id' element={<ProtectedRoute component={ComplaintDetail} requiredPermissions={['general.complaints_view']} />} />


                    </Routes>
                </Router>
            </ChatCountsProvider>
        </PermissionsProvider>

    );
};

export default App;
